import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Table,
  Card,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Container,
  Pagination,
  Button,
  Modal,
  Row,
  Col,
  Badge
} from 'react-bootstrap';
import {
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useListRequest } from '../hooks/useListRequest';
import { useScopedPage } from '../hooks/useScopedPage';
import SettingsPageHeader from '../components/SettingsPageHeader';
import { UserIcon, AssignmentIcon } from '../components/icons/SettingsIcons';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { authAPI, clearCache, departmentAPI, employeeAPI, isRequestCanceled } from '../utils/api';
import type { User, Role, CreateUserRequest, DepartmentAssignment, EmployeeAssignment } from '../types/auth';
import type { Department } from '../types/department';
import type { Employee } from '../types/employee';
import { formatDateTimeInCompanyTimezone } from '../utils/timezone';
import { validatePassword } from '../utils/passwordValidation';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const UserManagement: React.FC = () => {
  const { currentCompany, userRolePermissionsLoading } = useAuth();
  const {
    isSuperAdmin,
    canCreateUsers,
    canEditUsers,
    canDeleteUsers,
    canAssignUsers,
    canAccessUserManagement,
    canViewUsers
  } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'userManagement' });
  const { t: tc } = useTranslation('common');

  const isCurrentUserSuperAdmin = isSuperAdmin;
  const companyTimezone = currentCompany?.timezone || 'UTC';

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchState, setSearchState] = useState({ value: '', ready: true });
  const searchPending = !searchState.ready || searchState.value !== searchTerm;
  const [rowsPerPage] = useState(10);
  const [page, setPage] = useScopedPage(JSON.stringify([currentCompany?.company_id, searchTerm]));
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { begin, cancel } = useListRequest(JSON.stringify([currentCompany?.company_id, page, rowsPerPage, searchTerm, searchState.value, searchPending]));

  // Wait 300 milliseconds after typing stops before the server search runs.
  useEffect(() => {
    const timer = setTimeout(() => setSearchState({ value: searchTerm, ready: true }), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, currentCompany?.company_id]);

  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentUser, setAssignmentUser] = useState<User | null>(null);
  const [deptAssignments, setDeptAssignments] = useState<DepartmentAssignment[]>([]);
  const [empAssignments, setEmpAssignments] = useState<EmployeeAssignment[]>([]);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  const [allEmployees, setAllEmployees] = useState<Employee[]>([]);
  const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>([]);
  const [selectedEmpIds, setSelectedEmpIds] = useState<number[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentModalError, setAssignmentModalError] = useState<string | null>(null);
  const [assignmentModalSuccess, setAssignmentModalSuccess] = useState<string | null>(null);

  const sortedDepartments = useMemo(() => {
    const assignedIds = new Set(deptAssignments.map(a => a.department_id));
    return allDepartments
      .filter(d => !assignedIds.has(d.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDepartments, deptAssignments]);

  const sortedEmployees = useMemo(() => {
    const assignedIds = new Set(empAssignments.map(a => a.employee_id));
    return allEmployees
      .filter(e => !assignedIds.has(e.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allEmployees, empAssignments]);

  const [formData, setFormData] = useState<Omit<CreateUserRequest, 'company_id'>>({
    name: '',
    email: '',
    password: '',
    status: 1,
    superAdminFlag: 0,
    role_id: undefined
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState<Omit<CreateUserRequest, 'company_id'>>({
    name: '',
    email: '',
    password: '',
    status: 1,
    superAdminFlag: 0,
    role_id: undefined
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const [currentCompanyRoles, setCurrentCompanyRoles] = useState<Role[]>([]);
  const [currentCompanyRolesLoading, setCurrentCompanyRolesLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    if (!currentCompany || !canViewUsers) return;
    const { signal } = begin();
    if (signal.aborted) return;

    // Invalidate old responses immediately, but wait for the final input before requesting data.
    setUsers([]);
    setTotalRecords(0);
    setTotalPages(0);
    setLoading(true);
    setError(null);
    if (searchPending) return;

    try {
      const response = await authAPI.getUsers(currentCompany.company_id, { page, per_page: rowsPerPage, search: searchState.value }, signal);
      if (signal.aborted) return;
      if (response.success) {
        const lastPage = Math.max(1, response.last_page ?? 1);
        if (page > lastPage) {
          setPage(lastPage);
          return;
        }
        const totalRecords = response.total ?? 0;
        setUsers(response.data || []);
        setTotalRecords(totalRecords);
        // Legacy responses can omit last_page; derive the page count from the record count.
        setTotalPages(response.last_page ?? Math.ceil(totalRecords / rowsPerPage));
      }
    } catch (error: any) {
      if (signal.aborted || isRequestCanceled(error)) return;
      console.error('Error fetching users:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [currentCompany?.company_id, page, rowsPerPage, searchTerm, searchState.value, searchPending, canViewUsers, begin, t]);

  useEffect(() => {
    if (canViewUsers) {
      fetchUsers();
    }
    return cancel;
  }, [fetchUsers, canViewUsers, cancel]);

  useEffect(() => {
    const loadCurrentCompanyRoles = async () => {
      if (currentCompany && canAssignUsers) {
        try {
          setCurrentCompanyRolesLoading(true);
          const response = await authAPI.getRoles(currentCompany.company_id);
          if (response.success) {
            setCurrentCompanyRoles(response.data);
          }
        } catch (error) {
          console.error('Error loading current company roles:', error);
        } finally {
          setCurrentCompanyRolesLoading(false);
        }
      } else {
        setCurrentCompanyRoles([]);
      }
    };

    loadCurrentCompanyRoles();
  }, [currentCompany, canAssignUsers]);

  const paginatedUsers = users;

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      email: '',
      password: '',
      status: 1,
      superAdminFlag: 0,
      role_id: undefined
    });
  }, []);

  const handleShowModal = useCallback(() => {
    resetForm();
    setIsEditing(false);
    setEditingUser(null);
    setModalError(null);
    setModalSuccess(null);
    setShowModal(true);
  }, [resetForm]);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setIsEditing(false);
    setEditingUser(null);
    setModalError(null);
    setModalSuccess(null);
    setError(null);
  }, []);

  const handleEditClick = useCallback((user: User) => {
    setEditingUser(user);
    setIsEditing(true);
    setEditFormData({
      name: user.name,
      email: user.email,
      password: '',
      status: user.status,
      superAdminFlag: user.superAdminFlag || 0,
      role_id: user.role_id || undefined
    });
    setModalError(null);
    setModalSuccess(null);
    setError(null);
    setShowModal(true);
  }, []);

  const handleDeleteClick = useCallback((user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
    setError(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setError(null);
  }, []);

  const fetchAssignments = useCallback(async (userId: number) => {
    if (!currentCompany) return;

    try {
      setAssignmentLoading(true);
      const [deptRes, empRes] = await Promise.all([
        authAPI.getDepartmentAssignments(currentCompany.company_id, userId),
        authAPI.getEmployeeAssignments(currentCompany.company_id, userId)
      ]);

      if (deptRes.success) setDeptAssignments(deptRes.data);
      if (empRes.success) setEmpAssignments(empRes.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setAssignmentLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    if (assignmentModalSuccess) {
      const timer = setTimeout(() => setAssignmentModalSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [assignmentModalSuccess]);

  const handleManageAssignments = useCallback(async (user: User) => {
    if (!currentCompany) return;
    setAssignmentUser(user);
    setShowAssignmentModal(true);
    setAssignmentModalError(null);
    setAssignmentModalSuccess(null);
    setSelectedDeptIds([]);
    setSelectedEmpIds([]);

    fetchAssignments(user.id);

    try {
      const [deptRes, empRes] = await Promise.all([
        departmentAPI.getDepartments(currentCompany.company_id),
        employeeAPI.getEmployees(currentCompany.company_id)
      ]);
      if (deptRes && deptRes.departments) setAllDepartments(deptRes.departments);
      if (empRes && empRes.success) setAllEmployees(empRes.employees);
    } catch (error) {
      console.error('Error fetching depts/emps:', error);
    }
  }, [currentCompany, fetchAssignments]);

  const handleCloseAssignmentModal = useCallback(() => {
    setShowAssignmentModal(false);
    setAssignmentUser(null);
    setAssignmentModalError(null);
    setAssignmentModalSuccess(null);
    setSelectedDeptIds([]);
    setSelectedEmpIds([]);
    setDeptAssignments([]);
    setEmpAssignments([]);
  }, []);

  const handleAssignDepartments = async () => {
    if (!currentCompany || !assignmentUser || selectedDeptIds.length === 0) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.assignDepartments(currentCompany.company_id, {
        user_id: assignmentUser.id,
        department_ids: selectedDeptIds
      });
      if (response.success) {
        setAssignmentModalSuccess(t('assignSuccess'));
        setSelectedDeptIds([]);
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleRemoveDepartmentAssignment = async (assignmentId: number) => {
    if (!currentCompany || !assignmentUser) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.removeDepartmentAssignment(currentCompany.company_id, assignmentId);
      if (response.success) {
        setAssignmentModalSuccess(t('removeSuccess'));
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignEmployees = async () => {
    if (!currentCompany || !assignmentUser || selectedEmpIds.length === 0) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.assignEmployees(currentCompany.company_id, {
        user_id: assignmentUser.id,
        employee_ids: selectedEmpIds
      });
      if (response.success) {
        setAssignmentModalSuccess(t('assignSuccess'));
        setSelectedEmpIds([]);
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleRemoveEmployeeAssignment = async (assignmentId: number) => {
    if (!currentCompany || !assignmentUser) return;

    try {
      setAssignmentLoading(true);
      setAssignmentModalError(null);
      const response = await authAPI.removeEmployeeAssignment(currentCompany.company_id, assignmentId);
      if (response.success) {
        setAssignmentModalSuccess(t('removeSuccess'));
        fetchAssignments(assignmentUser.id);
      }
    } catch (error: any) {
      setAssignmentModalError(error.response?.data?.message || t('saveFailed'));
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleFormChange = useCallback((field: keyof Omit<CreateUserRequest, 'company_id'>, value: string | number | undefined) => {
    if (isEditing) {
      setEditFormData(prev => {
        const newData = { ...prev, [field]: value };
        if (field === 'superAdminFlag' && value === 1 && !isCurrentUserSuperAdmin) {
          newData.superAdminFlag = 0;
        }
        if (field === 'superAdminFlag' && value === 1) {
          newData.role_id = undefined;
        }
        return newData;
      });
    } else {
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        if (field === 'superAdminFlag' && value === 1 && !isCurrentUserSuperAdmin) {
          newData.superAdminFlag = 0;
        }
        if (field === 'superAdminFlag' && value === 1) {
          newData.role_id = undefined;
        }
        return newData;
      });
    }

    if (modalError) setModalError(null);
    if (modalSuccess) setModalSuccess(null);
  }, [modalError, modalSuccess, isCurrentUserSuperAdmin, isEditing]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const currentFormData = isEditing ? editFormData : formData;

    if (!currentFormData.name.trim() || !currentFormData.email.trim() || (!isEditing && !currentFormData.password.trim())) {
      setModalError(t('fillRequired'));
      return;
    }

    if (!currentCompany) {
      setModalError(t('selectCompanyError'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(currentFormData.email)) {
      setModalError(t('validEmailError'));
      return;
    }

    if (currentFormData.password) {
      const validation = validatePassword(currentFormData.password);
      if (!validation.valid) {
        setModalError(validation.error || t('invalidPassword'));
        return;
      }
    }

    if (currentFormData.superAdminFlag === 1 && !isCurrentUserSuperAdmin) {
      setModalError(t('noSuperAdminPermission'));
      return;
    }

    try {
      setModalLoading(true);
      setModalError(null);

      if (isEditing && editingUser) {
        const payload: Partial<CreateUserRequest> = {
          name: editFormData.name,
          email: editFormData.email,
          status: editFormData.status,
          superAdminFlag: editFormData.superAdminFlag,
          role_id: editFormData.role_id
        };

        if (editFormData.password.trim()) {
          payload.password = editFormData.password;
        }

        const response = await authAPI.updateUser(currentCompany.company_id, editingUser.id, payload);
        if (response.success) {
          setModalSuccess(response.message || t('updateSuccess'));
          clearCache(`usersByCompany_${currentCompany.company_id}`);
          fetchUsers();
          setTimeout(() => handleCloseModal(), 2000);
        } else {
          setModalError(response.message || t('saveFailed'));
        }
      } else {
        const requestData: CreateUserRequest = {
          ...formData,
          company_id: currentCompany.company_id
        };

        if (!canAssignUsers || !formData.role_id) {
          delete requestData.role_id;
        }

        if (!isCurrentUserSuperAdmin) {
          requestData.superAdminFlag = 0;
        }

        const response = await authAPI.createUser(requestData);

        setModalSuccess(response.message || t('createSuccess'));
        clearCache(`usersByCompany_${currentCompany.company_id}`);
        fetchUsers();
        setTimeout(() => handleCloseModal(), 2000);
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      setModalError(error.response?.data?.message || error.message || t('saveFailed'));
    } finally {
      setModalLoading(false);
    }
  }, [formData, editFormData, isEditing, editingUser, isCurrentUserSuperAdmin, currentCompany, canAssignUsers, fetchUsers, handleCloseModal, t]);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete || !currentCompany) return;

    try {
      setModalLoading(true);
      const response = await authAPI.deleteUser(currentCompany.company_id, userToDelete.id);
      if (response.success) {
        clearCache(`usersByCompany_${currentCompany.company_id}`);
        fetchUsers();
        handleCloseDeleteModal();
      }
    } catch (error: any) {
      console.error('Error deleting user:', error);
      if (error.response?.status === 409) {
        setError(error.response?.data?.message || t('cannotDeleteUser'));
      } else {
        setError(error.response?.data?.message || error.message || t('deleteFailed'));
      }
      setShowDeleteModal(false);
      setUserToDelete(null);
    } finally {
      setModalLoading(false);
    }
  }, [userToDelete, currentCompany, fetchUsers, handleCloseDeleteModal, t]);

  if (userRolePermissionsLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="text-center an-enterprise-page">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  if (!canAccessUserManagement) {
    return (
      <Layout title={t('title')}>
        <Container className="an-enterprise-page">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  const settingsPath = currentCompany ? `/company/${currentCompany.company_id}/settings` : '/settings';

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-data-page an-settings-page an-page-shell">
        <SettingsPageHeader
          title={t('title')}
          settingsPath={settingsPath}
          action={canCreateUsers && currentCompany ? (
            <Button variant="primary" onClick={handleShowModal}>
              <i className="bi bi-plus-lg"></i> {t('addUser')}
            </Button>
          ) : undefined}
        />

        {!currentCompany && (
          <Alert variant="warning" role="alert">{t('selectCompanyWarning')}</Alert>
        )}

        {currentCompany && (
          <>
            <Card className="mb-4 shadow-sm an-panel an-settings-form-card">
              <Card.Body>
                <InputGroup>
                  <InputGroup.Text id="user-search-label"><i className="bi bi-search"></i></InputGroup.Text>
                  <Form.Control
                    placeholder={t('searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setSearchState(previous => ({ ...previous, ready: false }));
                      setPage(1);
                    }}
                    aria-label={t('searchPlaceholder')}
                    aria-describedby="user-search-label"
                  />
                  {searchTerm && (
                    <Button variant="outline-secondary" onClick={() => {
                      setSearchTerm('');
                      setSearchState(previous => ({ ...previous, ready: false }));
                      setPage(1);
                    }} aria-label={tc('buttons.clear')}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  )}
                </InputGroup>
              </Card.Body>
            </Card>

            {error && <Alert variant="danger" role="alert">{error}</Alert>}

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">{t('loading')}</p>
              </div>
            ) : (
              <Card className="shadow-sm border-0 an-settings-table-card">
                <div className="d-md-none p-3">
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                      <Card key={user.id} className="an-data-card border-0 rounded-4 mb-3">
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                            <div className="d-flex align-items-center gap-2 min-w-0">
                              <UserIcon />
                              <div>
                                <div className="fw-medium">{user.name}</div>
                                <div className="small text-muted">{user.email}</div>
                              </div>
                            </div>
                            <Badge bg={user.status === 1 ? 'success' : 'secondary'}>
                              {user.status === 1 ? tc('status.active') : tc('status.inactive')}
                            </Badge>
                          </div>

                          <div className="small text-muted mb-2">
                            <div>{t('role')}: {user.role_name || 'N/A'}</div>
                            <div>{t('createdAt')}: {formatDateTimeInCompanyTimezone(user.created_at, companyTimezone)}</div>
                            <div>{t('updatedAt')}: {formatDateTimeInCompanyTimezone(user.updated_at, companyTimezone)}</div>
                          </div>

                          <div className="d-flex justify-content-end gap-2 flex-wrap">
                            {canAssignUsers && user.superAdminFlag !== 1 && (
                              <Button variant="link" className="p-1 text-info" onClick={() => handleManageAssignments(user)} title={t('manageAssignments')}>
                                <AssignmentIcon />
                              </Button>
                            )}
                            {canEditUsers && (
                              <Button variant="link" className="p-1 text-primary" onClick={() => handleEditClick(user)} title={t('editTitle')}>
                                <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
                              </Button>
                            )}
                            {canDeleteUsers && (
                              <Button variant="link" className="p-1 text-danger" onClick={() => handleDeleteClick(user)} title={t('deleteTitle')}>
                                <i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span>
                              </Button>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-5 text-muted">{searchTerm ? t('noMatchSearch') : t('noUsersForCompany')}</div>
                  )}
                </div>

                <div className="d-none d-md-block">
                  <div className="table-responsive an-data-table an-panel an-settings-table">
                    <Table hover className="mb-0 an-settings-table">
                      <thead className="bg-light">
                        <tr>
                          <th className="px-4">{t('nameColumn')}</th>
                          <th>{t('userEmail')}</th>
                          <th>{t('role')}</th>
                          <th>{tc('labels.status')}</th>
                          <th>{t('createdAt')}</th>
                          <th>{t('updatedAt')}</th>
                          {(canEditUsers || canDeleteUsers || canAssignUsers) && <th className="text-end px-4">{tc('labels.actions')}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.length > 0 ? (
                          paginatedUsers.map((user) => (
                            <tr key={user.id}>
                              <td className="px-4 py-3">
                                <div className="d-flex align-items-center">
                                  <UserIcon />
                                  <span className="fw-medium">{user.name}</span>
                                  {user.superAdminFlag === 1 && (
                                    <Badge bg="warning" text="dark" className="ms-2">{t('superAdminBadge')}</Badge>
                                  )}
                                </div>
                              </td>
                              <td className="py-3">
                                <small className="text-muted">{user.email}</small>
                              </td>
                              <td className="py-3">
                                <small>{user.role_name || 'N/A'}</small>
                              </td>
                              <td className="py-3">
                                <Badge bg={user.status === 1 ? 'success' : 'secondary'}>
                                  {user.status === 1 ? tc('status.active') : tc('status.inactive')}
                                </Badge>
                              </td>
                              <td><small className="text-muted">{formatDateTimeInCompanyTimezone(user.created_at, companyTimezone)}</small></td>
                              <td><small className="text-muted">{formatDateTimeInCompanyTimezone(user.updated_at, companyTimezone)}</small></td>
                              {(canEditUsers || canDeleteUsers || canAssignUsers) && (
                                <td className="text-end px-4 py-3">
                                  <div className="d-flex justify-content-end gap-2">
                                    {canAssignUsers && user.superAdminFlag !== 1 && (
                                      <Button
                                        variant="link"
                                        className="p-1 text-info"
                                        onClick={() => handleManageAssignments(user)}
                                        title={t('manageAssignments')}
                                      >
                                        <AssignmentIcon />
                                      </Button>
                                    )}
                                    {canEditUsers && (
                                      <Button
                                        variant="link"
                                        className="p-1 text-primary"
                                        onClick={() => handleEditClick(user)}
                                        title={t('editTitle')}
                                      >
                                        <i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span>
                                      </Button>
                                    )}
                                    {canDeleteUsers && (
                                      <Button
                                        variant="link"
                                        className="p-1 text-danger"
                                        onClick={() => handleDeleteClick(user)}
                                        title={t('deleteTitle')}
                                      >
                                        <i className="bi bi-trash"></i> <span className="ms-1">{tc('buttons.delete')}</span>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={(canEditUsers || canDeleteUsers || canAssignUsers) ? 7 : 6} className="text-center py-5 text-muted">
                              {searchTerm ? t('noMatchSearch') : t('noUsersForCompany')}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>
                {totalPages > 1 && (
                  <Card.Footer className="px-4 py-3 an-data-card">
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 overflow-auto">
                      <small className="text-muted text-nowrap">
                        {tc('messages.showing', { from: (page - 1) * rowsPerPage + 1, to: Math.min(page * rowsPerPage, totalRecords), total: totalRecords, entity: '' })}
                      </small>
                      <Pagination className="mb-0 flex-wrap justify-content-center an-pagination" aria-label={t('paginationLabel')}>
                        <Pagination.Prev disabled={page === 1} onClick={() => setPage(p => p - 1)} />
                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item key={i + 1} active={i + 1 === page} onClick={() => setPage(i + 1)}>
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            )}
          </>
        )}

        <Modal show={showModal} onHide={() => !modalLoading && handleCloseModal()} size="lg">
          <Modal.Header closeButton={!modalLoading}>
            <Modal.Title>{isEditing ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit} autoComplete="off">
            <Modal.Body className="px-4 an-settings-page">
              {modalError && <Alert variant="danger" role="alert">{modalError}</Alert>}
              {modalSuccess && <Alert variant="success" role="status">{modalSuccess}</Alert>}

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('fullNameLabel')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('namePlaceholder')}
                      value={isEditing ? editFormData.name : formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      required
                      disabled={modalLoading}
                      autoComplete="new-password"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{t('emailAddressLabel')} <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      value={isEditing ? editFormData.email : formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      required
                      disabled={modalLoading}
                      autoComplete="new-password"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold text-dark">{t('companyLabel')}</Form.Label>
                <Form.Control
                  type="text"
                  value={currentCompany ? `${currentCompany.description} (${currentCompany.company_id})` : ''}
                  disabled
                />
              </Form.Group>

              <Row className="mb-3">
                <Col md={isCurrentUserSuperAdmin && canAssignUsers ? 4 : 6}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">{tc('labels.status')}</Form.Label>
                    <Form.Select
                      value={isEditing ? editFormData.status : formData.status}
                      onChange={(e) => handleFormChange('status', Number(e.target.value))}
                      disabled={modalLoading}
                    >
                      <option value={1}>{tc('status.active')}</option>
                      <option value={0}>{tc('status.inactive')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                {isCurrentUserSuperAdmin && (
                  <Col md={canAssignUsers ? 4 : 6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">{t('adminLevel')}</Form.Label>
                      <Form.Select
                        value={isEditing ? editFormData.superAdminFlag : formData.superAdminFlag}
                        onChange={(e) => handleFormChange('superAdminFlag', Number(e.target.value))}
                        disabled={modalLoading}
                      >
                        <option value={0}>{t('regularUser')}</option>
                        <option value={1}>{t('superAdminBadge')}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                {canAssignUsers && (
                  <Col md={isCurrentUserSuperAdmin ? 4 : 6}>
                    <Form.Group>
                      <Form.Label className="fw-bold text-dark">{t('roleOptional')}</Form.Label>
                      <Form.Select
                        value={isEditing ? (editFormData.role_id || '') : (formData.role_id || '')}
                        onChange={(e) => handleFormChange('role_id', e.target.value ? Number(e.target.value) : undefined)}
                        disabled={modalLoading || currentCompanyRolesLoading || (isEditing ? editFormData.superAdminFlag === 1 : formData.superAdminFlag === 1)}
                      >
                        <option value="">{t('noRole')}</option>
                        {currentCompanyRoles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.role_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold text-dark">
                      {t('passwordLabel')} {!isEditing && <span className="text-danger">*</span>}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      placeholder={isEditing ? t('passwordPlaceholderEdit') : t('passwordPlaceholderCreate')}
                      value={isEditing ? editFormData.password : formData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      required={!isEditing}
                      disabled={modalLoading}
                      autoComplete="new-password"
                    />
                    <Form.Text className="text-muted">
                      {t('passwordHint')}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="px-4 an-section-divider">
              {!isEditing && (
                <Button variant="outline-secondary" onClick={() => { resetForm(); setModalError(null); setModalSuccess(null); }} disabled={modalLoading}>
                  {t('reset')}
                </Button>
              )}
              <Button variant="outline-secondary" onClick={() => handleCloseModal()} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={modalLoading}>
                {modalLoading ? <Spinner animation="border" size="sm" /> : (isEditing ? t('saveChanges') : t('createUserBtn'))}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Assignment Modal */}
        <Modal show={showAssignmentModal} onHide={() => !assignmentLoading && handleCloseAssignmentModal()} size="lg">
          <Modal.Header closeButton={!assignmentLoading}>
            <Modal.Title>{t('assignmentsModalTitle')}: {assignmentUser?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-4 an-settings-page">
            {assignmentModalError && <Alert variant="danger" role="alert">{assignmentModalError}</Alert>}
            {assignmentModalSuccess && <Alert variant="success" role="status">{assignmentModalSuccess}</Alert>}

            <Row>
              <Col md={6} className="border-end">
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <AssignmentIcon /> {t('deptAssignments')}
                </h6>
                <div className="mb-4">
                  <Autocomplete
                    multiple
                    size="small"
                    options={sortedDepartments}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={allDepartments.filter(d => selectedDeptIds.includes(d.id))}
                    onChange={(_, newValue) => {
                      setSelectedDeptIds(newValue.map(v => v.id));
                    }}
                    slotProps={{
                      popper: {
                        sx: { zIndex: 3000 }
                      }
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('selectDepts')}
                        variant="outlined"
                        placeholder={t('deptAssignments')}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                    disabled={assignmentLoading}
                  />
                  <Button
                    variant="primary"
                    className="mt-2 w-100"
                    onClick={handleAssignDepartments}
                    disabled={assignmentLoading || selectedDeptIds.length === 0}
                  >
                    {t('assignBtn')}
                  </Button>
                </div>

                <Typography variant="subtitle2" gutterBottom className="text-muted fw-bold">
                  {t('currentAssignments')}
                </Typography>
                <div className="assignment-list overflow-auto p-2 border rounded bg-light" style={{ maxHeight: '250px', minHeight: '100px' }}>
                  {assignmentLoading && deptAssignments.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : deptAssignments.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {deptAssignments.map(assignment => (
                        <Badge key={assignment.id} bg="info" className="d-flex align-items-center p-2 shadow-sm">
                          <span className="me-2">{assignment.department?.name || `${t('idLabel')}: ${assignment.department_id}`}</span>
                          <span
                            className="ms-1 cursor-pointer bg-white text-info rounded-circle d-inline-flex align-items-center justify-content-center"
                            style={{ cursor: 'pointer', width: '16px', height: '16px', fontSize: '12px' }}
                            onClick={() => handleRemoveDepartmentAssignment(assignment.id)}
                            title={t('removeBtn')}
                          >
                            &times;
                          </span>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted small">{t('noDeptAssignments')}</div>
                  )}
                </div>
              </Col>

              <Col md={6}>
                <h6 className="fw-bold mb-3 d-flex align-items-center">
                  <UserIcon /> {t('empAssignments')}
                </h6>
                <div className="mb-4">
                  <Autocomplete
                    multiple
                    size="small"
                    options={sortedEmployees}
                    disableCloseOnSelect
                    getOptionLabel={(option) => `${option.name} (${option.emp_id})`}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={allEmployees.filter(e => selectedEmpIds.includes(e.id))}
                    onChange={(_, newValue) => {
                      setSelectedEmpIds(newValue.map(v => v.id));
                    }}
                    slotProps={{
                      popper: {
                        sx: { zIndex: 3000 }
                      }
                    }}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} key={option.id}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        <Box>
                          <Typography variant="body2" className="fw-bold">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.emp_id}</Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('selectEmps')}
                        variant="outlined"
                        placeholder={t('empAssignments')}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={option.id}
                            label={option.name}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                    disabled={assignmentLoading}
                  />
                  <Button
                    variant="primary"
                    className="mt-2 w-100"
                    onClick={handleAssignEmployees}
                    disabled={assignmentLoading || selectedEmpIds.length === 0}
                  >
                    {t('assignBtn')}
                  </Button>
                </div>

                <Typography variant="subtitle2" gutterBottom className="text-muted fw-bold">
                  {t('currentAssignments')}
                </Typography>
                <div className="assignment-list overflow-auto p-2 border rounded bg-light" style={{ maxHeight: '250px', minHeight: '100px' }}>
                  {assignmentLoading && empAssignments.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : empAssignments.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {empAssignments.map(assignment => (
                        <Badge key={assignment.id} bg="success" className="d-flex align-items-center p-2 shadow-sm">
                          <span className="me-2">{assignment.employee?.name || `${t('idLabel')}: ${assignment.employee_id}`}</span>
                          <span
                            className="ms-1 cursor-pointer bg-white text-success rounded-circle d-inline-flex align-items-center justify-content-center"
                            style={{ cursor: 'pointer', width: '16px', height: '16px', fontSize: '12px' }}
                            onClick={() => handleRemoveEmployeeAssignment(assignment.id)}
                            title={t('removeBtn')}
                          >
                            &times;
                          </span>
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted small">{t('noEmpAssignments')}</div>
                  )}
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseAssignmentModal} disabled={assignmentLoading}>
              {tc('buttons.close')}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showDeleteModal} onHide={() => !modalLoading && handleCloseDeleteModal()} centered>
          <Modal.Header closeButton={!modalLoading}>
            <Modal.Title>{t('confirmDeleteTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('confirmDeleteMessage', { name: userToDelete?.name })}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleCloseDeleteModal} disabled={modalLoading}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : t('deleteUserBtn')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default UserManagement;

